import React, { forwardRef, useImperativeHandle, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  AlertTriangle,
  BookOpen,
  FileSpreadsheet,
  Layers3,
  Pencil,
  Plus,
  Search,
  Trash2,
  UsersRound,
} from 'lucide-react';
import ListItemCard from '../../../components/common/ListItemCard';
import { Button, cn } from '../../../components/ui/Button';
import { Card } from '../../../components/ui/Card';
import { Input } from '../../../components/ui/Input';
import FullScreenModal from '../../../components/ui/Modal';
import Toast, { ToastMessage } from '../../../components/ui/Toast';
import { Trimester } from '../context/TrimestersContext';
import { useLessons, useMagazines, useSegments } from '../hooks';
import { MagazineImportField, MagazineImportPreview, MagazineImportResult } from '../services/magazinesService';
import { Magazine, MagazineLesson, Segment } from '../types/trimestersSettings.types';

interface GeneralSettingsPanelProps {
  trimesters: Trimester[];
  currentTrimesterId: string;
  mode?: 'magazines' | 'segments';
  showHeader?: boolean;
  showSegmentCreateButton?: boolean;
}

export interface GeneralSettingsPanelRef {
  openCreateSegment: () => void;
}

type MagazineFormState = {
  id?: string;
  title: string;
  trimesterId: string;
  segmentId: string;
  description: string;
  lessons: MagazineLesson[];
};

type SegmentFormState = {
  id?: string;
  name: string;
  ageStart: string;
  ageEnd: string;
};

const emptySegmentForm: SegmentFormState = {
  name: '',
  ageStart: '',
  ageEnd: '',
};

const normalizeImportLabel = (value: string) =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();

const importFieldKeywords: Record<string, string[]> = {
  magazine_title: ['revista', 'titulo da revista', 'magazine'],
  segment_name: ['segmento', 'segment'],
  lesson_number: ['licao', 'numero da licao', 'n licao', 'lesson number'],
  lesson_title: ['titulo da licao', 'licao titulo', 'titulo', 'lesson title'],
  lesson_summary: ['resumo', 'descricao', 'summary'],
  biblical_reference: ['referencia', 'texto biblico', 'biblical'],
  magazine_description: ['descricao da revista', 'observacao revista'],
};

const segmentTone = (index: number) =>
  [
    'bg-rose-50 text-rose-700',
    'bg-sky-50 text-sky-700',
    'bg-emerald-50 text-emerald-700',
    'bg-amber-50 text-amber-700',
    'bg-violet-50 text-violet-700',
  ][index % 5];

const getErrorMessage = (error: unknown, fallback: string) => (error instanceof Error ? error.message : fallback);

const GeneralSettingsPanel = forwardRef<GeneralSettingsPanelRef, GeneralSettingsPanelProps>(({
  trimesters,
  currentTrimesterId,
  mode,
  showHeader = true,
  showSegmentCreateButton = true,
}, ref) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const settingsTab = mode ?? searchParams.get('settingsTab') ?? 'magazines';
  const magazineSearch = searchParams.get('magazineSearch') ?? '';
  const segmentFilter = searchParams.get('segment') ?? 'all';

  const { segments, isLoading: isLoadingSegments, error: segmentsError, saveSegment, removeSegment } = useSegments();
  const {
    magazines,
    error: magazinesError,
    saveMagazine,
    removeMagazine,
    loadImportFields,
    previewImport,
    importLessons,
  } = useMagazines();
  const { buildLessons } = useLessons();

  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [magazineForm, setMagazineForm] = useState<MagazineFormState | null>(null);
  const [segmentForm, setSegmentForm] = useState<SegmentFormState | null>(null);
  const [deleteMagazine, setDeleteMagazine] = useState<Magazine | null>(null);
  const [deleteSegment, setDeleteSegment] = useState<Segment | null>(null);
  const [importOpen, setImportOpen] = useState(false);
  const [importFields, setImportFields] = useState<MagazineImportField[]>([]);
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importPreview, setImportPreview] = useState<MagazineImportPreview | null>(null);
  const [importMapping, setImportMapping] = useState<Record<string, string>>({});
  const [importResult, setImportResult] = useState<MagazineImportResult | null>(null);
  const [importError, setImportError] = useState<string | null>(null);
  const [magazineError, setMagazineError] = useState<string | null>(null);
  const [segmentError, setSegmentError] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [operationNotice, setOperationNotice] = useState<ToastMessage | null>(null);
  const [isImporting, setIsImporting] = useState(false);

  const showOperationNotice = (type: ToastMessage['type'], message: string) => {
    setOperationNotice({ type, message });
  };

  const closeMagazineModal = () => {
    setMagazineForm(null);
    setMagazineError(null);
  };

  const closeSegmentModal = () => {
    setSegmentForm(null);
    setSegmentError(null);
  };

  const closeDeleteModal = () => {
    setDeleteMagazine(null);
    setDeleteSegment(null);
    setDeleteError(null);
  };

  const openCreateSegment = () => {
    setSegmentError(null);
    setSegmentForm(emptySegmentForm);
  };

  useImperativeHandle(ref, () => ({
    openCreateSegment,
  }));

  const updateSearchParam = (key: string, value: string) => {
    const next = new URLSearchParams(searchParams);
    if (!value || value === 'all') {
      next.delete(key);
    } else {
      next.set(key, value);
    }
    setSearchParams(next);
  };

  const openImportModal = async () => {
    setImportOpen(true);
    setImportError(null);
    setImportResult(null);

    if (importFields.length > 0) return;

    try {
      const fields = await loadImportFields();
      setImportFields(fields);
    } catch (error) {
      const message = getErrorMessage(error, 'Nao foi possivel carregar os campos de importacao.');
      setImportError(message);
      showOperationNotice('error', message);
    }
  };

  const buildInitialImportMapping = (fields: MagazineImportField[], columns: string[]) =>
    fields.reduce<Record<string, string>>((mapping, field) => {
      const keywords = importFieldKeywords[field.key] ?? [];
      const matchedColumn =
        columns.find((column) => keywords.includes(normalizeImportLabel(column))) ??
        columns.find((column) => keywords.some((keyword) => normalizeImportLabel(column).includes(keyword))) ??
        '';

      mapping[field.key] = matchedColumn;
      return mapping;
    }, {});

  const handleImportFileChange = async (file: File) => {
    setImportFile(file);
    setImportPreview(null);
    setImportResult(null);
    setImportError(null);
    setIsImporting(true);

    try {
      const fields = importFields.length > 0 ? importFields : await loadImportFields();
      const preview = await previewImport(file);
      setImportFields(fields);
      setImportPreview(preview);
      setImportMapping(buildInitialImportMapping(fields, preview.columns));
    } catch (error) {
      const message = getErrorMessage(error, 'Nao foi possivel ler a planilha.');
      setImportError(message);
      showOperationNotice('error', message);
    } finally {
      setIsImporting(false);
    }
  };

  const handleImportLessons = async () => {
    if (!importFile) return;

    setImportError(null);
    setImportResult(null);
    setIsImporting(true);

    try {
      const result = await importLessons({
        file: importFile,
        trimesterId: currentTrimesterId,
        mapping: importMapping,
      });
      setImportResult(result);
      showOperationNotice('success', `Importacao concluida: ${result.magazines} revistas e ${result.lessons} lições.`);
    } catch (error) {
      const message = getErrorMessage(error, 'Nao foi possivel importar as lições.');
      setImportError(message);
      showOperationNotice('error', message);
    } finally {
      setIsImporting(false);
    }
  };

  const setSettingsTab = (tab: string) => {
    const next = new URLSearchParams(searchParams);
    next.set('settingsTab', tab);
    setSearchParams(next);
  };

  const segmentById = useMemo(() => new Map(segments.map((segment) => [segment.id, segment])), [segments]);
  const trimesterById = useMemo(() => new Map(trimesters.map((trimester) => [trimester.id, trimester])), [trimesters]);

  const filteredMagazines = useMemo(() => {
    const query = magazineSearch.trim().toLowerCase();

    return magazines.filter((magazine) => {
      const segment = segmentById.get(magazine.segmentId);
      const matchesQuery =
        !query ||
        magazine.title.toLowerCase().includes(query) ||
        magazine.description.toLowerCase().includes(query) ||
        segment?.name.toLowerCase().includes(query);
      const matchesSegment = segmentFilter === 'all' || magazine.segmentId === segmentFilter;
      const matchesTrimester = magazine.trimesterId === currentTrimesterId;

      return matchesQuery && matchesSegment && matchesTrimester;
    });
  }, [currentTrimesterId, magazineSearch, magazines, segmentById, segmentFilter]);

  const openCreateMagazine = async () => {
    setMagazineError(null);
    const lessons = await buildLessons(`draft-${Date.now()}`);
    setMagazineForm({
      title: '',
      trimesterId: currentTrimesterId,
      segmentId: segments[0]?.id ?? '',
      description: '',
      lessons,
    });
  };

  const openEditMagazine = (magazine: Magazine) => {
    setMagazineError(null);
    setMagazineForm({
      ...magazine,
      lessons: magazine.lessons.map((lesson) => ({ ...lesson })),
    });
  };

  const handleSaveMagazine = async () => {
    if (!magazineForm) return;

    const isEditing = Boolean(magazineForm.id);
    if (!magazineForm.title.trim() || !magazineForm.segmentId || !magazineForm.trimesterId) {
      const message = 'Preencha titulo, trimestre e segmento antes de salvar a revista.';
      setMagazineError(message);
      showOperationNotice('error', message);
      return;
    }

    if (!trimesters.some((trimester) => trimester.id === magazineForm.trimesterId)) {
      const message = 'Selecione um trimestre valido antes de salvar a revista.';
      setMagazineError(message);
      showOperationNotice('error', message);
      return;
    }

    setMagazineError(null);

    try {
      await saveMagazine({
        id: magazineForm.id,
        title: magazineForm.title.trim(),
        trimesterId: magazineForm.trimesterId,
        segmentId: magazineForm.segmentId,
        description: magazineForm.description.trim(),
        lessons: magazineForm.lessons,
      });
      showOperationNotice('success', isEditing ? 'Revista atualizada com sucesso.' : 'Revista cadastrada com sucesso.');
      closeMagazineModal();
    } catch (error) {
      const message = getErrorMessage(error, 'Nao foi possivel salvar a revista.');
      setMagazineError(message);
      showOperationNotice('error', message);
    }
  };

  const handleSaveSegment = async () => {
    if (!segmentForm) return;

    const isEditing = Boolean(segmentForm.id);
    if (!segmentForm.name.trim()) {
      const message = 'Informe o nome do segmento antes de salvar.';
      setSegmentError(message);
      showOperationNotice('error', message);
      return;
    }

    setSegmentError(null);

    try {
      await saveSegment({
        id: segmentForm.id,
        name: segmentForm.name.trim(),
        ageStart: Number(segmentForm.ageStart),
        ageEnd: Number(segmentForm.ageEnd),
      });
      showOperationNotice('success', isEditing ? 'Segmento atualizado com sucesso.' : 'Segmento cadastrado com sucesso.');
      closeSegmentModal();
    } catch (error) {
      const message = getErrorMessage(error, 'Nao foi possivel salvar o segmento.');
      setSegmentError(message);
      showOperationNotice('error', message);
    }
  };

  const handleRemoveItem = async () => {
    const removingMagazine = deleteMagazine !== null;

    setDeleteError(null);

    try {
      if (deleteMagazine) {
        await removeMagazine(deleteMagazine.id);
      }
      if (deleteSegment) {
        await removeSegment(deleteSegment.id);
      }
      showOperationNotice(
        'success',
        removingMagazine ? 'Revista removida com sucesso.' : 'Segmento removido com sucesso.'
      );
      closeDeleteModal();
    } catch (error) {
      const message = getErrorMessage(
        error,
        removingMagazine ? 'Nao foi possivel remover a revista.' : 'Nao foi possivel remover o segmento.'
      );
      setDeleteError(message);
      showOperationNotice('error', message);
    }
  };

  const handleLessonChange = (lessonNumber: number, updates: Partial<MagazineLesson>) => {
    setMagazineForm((current) => {
      if (!current) return current;

      return {
        ...current,
        lessons: current.lessons.map((lesson) =>
          lesson.number === lessonNumber ? { ...lesson, ...updates } : lesson
        ),
      };
    });
  };

  return (
    <section className="space-y-5">
      <Toast notice={operationNotice} onClose={() => setOperationNotice(null)} />

      {showHeader && (
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="text-lg font-bold text-slate-900">Configuracoes gerais</h3>
            <p className="text-sm text-gray-500">Revistas, segmentos, lições e importacao do trimestre.</p>
          </div>
        </div>
      )}

      {!mode && (
        <div className="grid grid-cols-2 gap-2 rounded-2xl bg-slate-100 p-1">
          {[
            { id: 'magazines', label: 'Revistas' },
            { id: 'segments', label: 'Segmentos' },
          ].map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setSettingsTab(tab.id)}
              className={cn(
                'h-10 rounded-xl text-sm font-semibold transition-colors',
                settingsTab === tab.id ? 'bg-white text-primary shadow-sm' : 'text-slate-600'
              )}
            >
              {tab.label}
            </button>
          ))}
        </div>
      )}

      {settingsTab === 'magazines' ? (
        <div className="space-y-4">
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Input
                value={magazineSearch}
                onChange={(event) => updateSearchParam('magazineSearch', event.target.value)}
                placeholder="Buscar revista ou segmento..."
                className="pl-11"
              />
              <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
            </div>
            <Button className="h-12 w-12 px-0" onClick={openCreateMagazine}>
              <Plus size={18} />
            </Button>
          </div>

          {magazinesError && (
            <div className="rounded-xl border border-rose-100 bg-rose-50 p-3 text-sm font-medium text-rose-700">
              {magazinesError}
            </div>
          )}

          <div className="flex gap-2 overflow-x-auto pb-1">
            <button
              type="button"
              onClick={() => updateSearchParam('segment', 'all')}
              className={cn(
                'shrink-0 rounded-full px-3 py-2 text-xs font-semibold',
                segmentFilter === 'all' ? 'bg-primary text-white' : 'bg-slate-100 text-slate-600'
              )}
            >
              Todos
            </button>
            {segments.map((segment, index) => (
              <button
                key={segment.id}
                type="button"
                onClick={() => updateSearchParam('segment', segment.id)}
                className={cn(
                  'shrink-0 rounded-full px-3 py-2 text-xs font-semibold',
                  segmentFilter === segment.id ? 'bg-primary text-white' : segmentTone(index)
                )}
              >
                {segment.name}
              </button>
            ))}
          </div>

          <Card className="border-slate-200 p-4">
            <div className="space-y-3">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-sm font-bold text-slate-900">Importacao do trimestre</p>
                  <p className="text-xs text-gray-500">Arquivo .xlsx ou .csv com revista, segmento e 13 lições.</p>
                </div>
                <Button variant="secondary" className="h-10 gap-2 px-3" onClick={openImportModal}>
                  <FileSpreadsheet size={16} />
                  Importar
                </Button>
              </div>
              {importResult && (
                <div className="rounded-2xl border border-emerald-100 bg-emerald-50 p-3 text-sm text-emerald-800">
                  Importacao concluida: {importResult.magazines} revistas e {importResult.lessons} lições.
                </div>
              )}
            </div>
          </Card>

          <div className="space-y-3">
            {filteredMagazines.map((magazine) => {
              const segment = segmentById.get(magazine.segmentId);

              return (
                <ListItemCard
                  key={magazine.id}
                  title={magazine.title}
                  subtitle={magazine.description}
                  leadingIcon={BookOpen}
                  leadingTone="from-amber-100 to-orange-100 text-amber-700"
                  badges={[
                    { label: segment?.name ?? 'Segmento removido', className: 'bg-sky-50 text-sky-700' },
                    { label: trimesterById.get(magazine.trimesterId)?.label ?? 'Trimestre removido', className: 'bg-slate-100 text-slate-700' },
                  ]}
                  menuOpen={openMenuId === magazine.id}
                  onToggleMenu={() => setOpenMenuId(openMenuId === magazine.id ? null : magazine.id)}
                  actions={[
                    {
                      label: 'Editar',
                      icon: Pencil,
                      tone: 'bg-sky-50 text-sky-700',
                      onClick: () => {
                        setOpenMenuId(null);
                        openEditMagazine(magazine);
                      },
                    },
                    {
                      label: 'Remover',
                      icon: Trash2,
                      tone: 'bg-rose-50 text-rose-700',
                      onClick: () => {
                        setOpenMenuId(null);
                        setDeleteError(null);
                        setDeleteMagazine(magazine);
                      },
                    },
                  ]}
                  metricLeft={{
                    icon: Layers3,
                    label: `${magazine.lessons.length} lições`,
                    tone: 'bg-emerald-50 text-emerald-700',
                  }}
                  metricRight={{
                    icon: BookOpen,
                    label: segment?.name ?? 'Segmento',
                    tone: 'bg-sky-50 text-sky-700',
                  }}
                />
              );
            })}

            {filteredMagazines.length === 0 && (
              <Card className="border-dashed border-slate-300 bg-slate-50 p-5 text-center">
                <p className="text-sm font-semibold text-slate-700">Nenhuma revista neste trimestre.</p>
                <p className="mt-1 text-sm text-slate-500">Cadastre uma revista para o segmento desejado.</p>
              </Card>
            )}
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          {showSegmentCreateButton && (
            <Button className="h-12 w-full gap-2" onClick={openCreateSegment}>
              <Plus size={18} />
              Novo segmento
            </Button>
          )}

          {segmentsError && (
            <div className="rounded-xl border border-rose-100 bg-rose-50 p-3 text-sm font-medium text-rose-700">
              {segmentsError}
            </div>
          )}

          <div className="space-y-3">
            {segments.map((segment, index) => (
              <ListItemCard
                key={segment.id}
                title={segment.name}
                leadingIcon={UsersRound}
                leadingTone="from-sky-100 to-cyan-100 text-sky-700"
                badges={[{ label: `${segment.ageStart} a ${segment.ageEnd} anos`, className: segmentTone(index) }]}
                menuOpen={openMenuId === segment.id}
                onToggleMenu={() => setOpenMenuId(openMenuId === segment.id ? null : segment.id)}
                actions={[
                  {
                    label: 'Editar',
                    icon: Pencil,
                    tone: 'bg-sky-50 text-sky-700',
                    onClick: () => {
                      setOpenMenuId(null);
                      setSegmentError(null);
                      setSegmentForm({
                        id: segment.id,
                        name: segment.name,
                        ageStart: String(segment.ageStart),
                        ageEnd: String(segment.ageEnd),
                      });
                    },
                  },
                  {
                    label: 'Remover',
                    icon: Trash2,
                    tone: 'bg-rose-50 text-rose-700',
                    onClick: () => {
                      setOpenMenuId(null);
                      setDeleteError(null);
                      setDeleteSegment(segment);
                    },
                  },
                ]}
                metricLeft={{
                  icon: BookOpen,
                  label: `${segment.magazinesCount ?? magazines.filter((magazine) => magazine.segmentId === segment.id).length} revistas`,
                  tone: 'bg-amber-50 text-amber-700',
                }}
              />
            ))}

            {!isLoadingSegments && segments.length === 0 && (
              <Card className="border-dashed border-slate-300 bg-slate-50 p-5 text-center">
                <p className="text-sm font-semibold text-slate-700">Nenhum segmento cadastrado.</p>
              </Card>
            )}
          </div>
        </div>
      )}

      <FullScreenModal
        open={magazineForm !== null}
        onClose={closeMagazineModal}
        title={magazineForm?.id && magazines.some((item) => item.id === magazineForm.id) ? 'Editar revista' : 'Nova revista'}
        subtitle="Configure a revista do trimestre e suas 13 lições."
        footer={
          <div className="grid grid-cols-2 gap-3">
            <Button variant="ghost" className="h-12 border border-gray-200" onClick={closeMagazineModal}>
              Cancelar
            </Button>
            <Button className="h-12" onClick={handleSaveMagazine}>
              Salvar revista
            </Button>
          </div>
        }
      >
        {magazineForm && (
          <div className="space-y-4">
            {magazineError && (
              <div className="rounded-2xl border border-rose-100 bg-rose-50 p-3 text-sm font-semibold text-rose-900">
                {magazineError}
              </div>
            )}
            <Input
              label="Titulo"
              value={magazineForm.title}
              onChange={(event) => setMagazineForm((current) => current && { ...current, title: event.target.value })}
            />
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              {!mode && (
                <div className="space-y-1.5">
                  <label className="ml-1 text-sm font-medium text-gray-700">Trimestre</label>
                  <select
                    value={magazineForm.trimesterId}
                    onChange={(event) =>
                      setMagazineForm((current) => current && { ...current, trimesterId: event.target.value })
                    }
                    className="h-12 w-full rounded-xl border border-gray-200 bg-white px-4 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
                  >
                    {trimesters.map((trimester) => (
                      <option key={trimester.id} value={trimester.id}>
                        {trimester.label}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              <div className="space-y-1.5">
                <label className="ml-1 text-sm font-medium text-gray-700">Segmento</label>
                <select
                  value={magazineForm.segmentId}
                  onChange={(event) =>
                    setMagazineForm((current) => current && { ...current, segmentId: event.target.value })
                  }
                  className="h-12 w-full rounded-xl border border-gray-200 bg-white px-4 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
                >
                  {segments.map((segment) => (
                    <option key={segment.id} value={segment.id}>
                      {segment.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="space-y-1.5">
              <label className="ml-1 text-sm font-medium text-gray-700">Descricao</label>
              <textarea
                value={magazineForm.description}
                onChange={(event) =>
                  setMagazineForm((current) => current && { ...current, description: event.target.value })
                }
                className="min-h-[88px] w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
              />
            </div>

            <div className="space-y-3">
              <p className="ml-1 text-sm font-bold text-slate-900">LIções da revista</p>
              {magazineForm.lessons.map((lesson) => (
                <div key={lesson.id} className="space-y-3 rounded-2xl border border-slate-200 bg-slate-50 p-3">
                  <div className="flex items-center gap-2">
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-white text-xs font-bold text-primary">
                      {lesson.number}
                    </span>
                    <Input
                      value={lesson.title}
                      placeholder="Titulo da lição"
                      onChange={(event) => handleLessonChange(lesson.number, { title: event.target.value })}
                    />
                  </div>
                  <textarea
                    value={lesson.summary}
                    placeholder="Descrição ou resumo"
                    onChange={(event) => handleLessonChange(lesson.number, { summary: event.target.value })}
                    className="min-h-[72px] w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
                  />
                </div>
              ))}
            </div>
          </div>
        )}
      </FullScreenModal>

      <FullScreenModal
        open={segmentForm !== null}
        onClose={closeSegmentModal}
        title={segmentForm?.id ? 'Editar segmento' : 'Novo segmento'}
        subtitle="Cadastre a faixa de idade usada por turmas e revistas."
        footer={
          <div className="grid grid-cols-2 gap-3">
            <Button variant="ghost" className="h-12 border border-gray-200" onClick={closeSegmentModal}>
              Cancelar
            </Button>
            <Button className="h-12" onClick={handleSaveSegment}>
              Salvar segmento
            </Button>
          </div>
        }
      >
        {segmentForm && (
          <div className="space-y-4">
            {segmentError && (
              <div className="rounded-2xl border border-rose-100 bg-rose-50 p-3 text-sm font-semibold text-rose-900">
                {segmentError}
              </div>
            )}
            <Input
              label="Nome do segmento"
              value={segmentForm.name}
              onChange={(event) => setSegmentForm((current) => current && { ...current, name: event.target.value })}
            />
            <div className="grid grid-cols-2 gap-3">
              <Input
                label="Idade inicial"
                type="number"
                value={segmentForm.ageStart}
                onChange={(event) =>
                  setSegmentForm((current) => current && { ...current, ageStart: event.target.value })
                }
              />
              <Input
                label="Idade final"
                type="number"
                value={segmentForm.ageEnd}
                onChange={(event) => setSegmentForm((current) => current && { ...current, ageEnd: event.target.value })}
              />
            </div>
          </div>
        )}
      </FullScreenModal>

      <FullScreenModal
        open={importOpen}
        onClose={() => {
          setImportOpen(false);
          setImportError(null);
        }}
        title="Importar revistas"
        subtitle="Relacione as colunas da planilha com os campos das lições."
      >
        <div className="space-y-4">
          <label className="flex cursor-pointer flex-col items-center justify-center rounded-3xl border border-dashed border-slate-300 bg-slate-50 p-6 text-center">
            <FileSpreadsheet size={28} className="text-primary" />
            <span className="mt-3 text-sm font-semibold text-slate-800">Selecionar arquivo</span>
            <span className="mt-1 text-xs text-gray-500">Formatos aceitos: .xlsx ou .csv</span>
            <input
              type="file"
              accept=".xlsx,.csv"
              className="hidden"
              onChange={(event) => {
                const file = event.target.files?.[0];
                if (file) {
                  handleImportFileChange(file);
                }
              }}
            />
          </label>

          {importError && (
            <div className="rounded-2xl border border-rose-100 bg-rose-50 p-4 text-sm font-medium text-rose-700">
              {importError}
            </div>
          )}

          {importPreview && (
            <div className="rounded-2xl border border-emerald-100 bg-emerald-50 p-4">
              <p className="text-sm font-bold text-emerald-900">{importPreview.fileName}</p>
              <p className="mt-1 text-sm text-emerald-800">
                {importPreview.rowsCount} linhas encontradas para o trimestre selecionado.
              </p>
            </div>
          )}

          {importPreview && (
            <div className="space-y-3">
              {importFields.map((field) => (
                <div key={field.key} className="space-y-1.5">
                  <label className="ml-1 text-sm font-medium text-gray-700">
                    {field.label}
                    {field.required && <span className="text-rose-600"> *</span>}
                  </label>
                  <select
                    value={importMapping[field.key] ?? ''}
                    onChange={(event) =>
                      setImportMapping((current) => ({
                        ...current,
                        [field.key]: event.target.value,
                      }))
                    }
                    className="h-12 w-full rounded-xl border border-gray-200 bg-white px-4 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
                  >
                    <option value="">Nao importar</option>
                    {importPreview.columns.map((column) => (
                      <option key={column} value={column}>
                        {column}
                      </option>
                    ))}
                  </select>
                </div>
              ))}
            </div>
          )}

          {importResult && (
            <div className="rounded-2xl border border-emerald-100 bg-emerald-50 p-4 text-sm font-semibold text-emerald-800">
              {importResult.magazines} revistas e {importResult.lessons} lições importadas.
            </div>
          )}

          {importPreview && (
            <Button className="h-12 w-full" onClick={handleImportLessons} disabled={isImporting}>
              {isImporting ? 'Importando...' : 'Importar lições'}
            </Button>
          )}
        </div>
      </FullScreenModal>

      <FullScreenModal
        open={deleteMagazine !== null || deleteSegment !== null}
        onClose={closeDeleteModal}
        title={deleteMagazine ? 'Remover revista' : 'Remover segmento'}
        subtitle={deleteMagazine?.title ?? deleteSegment?.name}
        footer={
          <div className="grid grid-cols-2 gap-3">
            <Button
              variant="ghost"
              className="h-12 border border-gray-200"
              onClick={closeDeleteModal}
            >
              Cancelar
            </Button>
            <Button
              className="h-12 bg-rose-600 hover:bg-rose-700"
              onClick={handleRemoveItem}
            >
              Remover
            </Button>
          </div>
        }
      >
        <div className="space-y-3">
          {deleteError && (
            <div className="rounded-2xl border border-rose-100 bg-rose-50 p-3 text-sm font-semibold text-rose-900">
              {deleteError}
            </div>
          )}
          <div className="flex items-start gap-3 rounded-2xl border border-rose-100 bg-rose-50 p-4">
            <AlertTriangle size={20} className="shrink-0 text-rose-600" />
            <p className="text-sm text-rose-900">
              {deleteMagazine
                ? 'Esta acao remove a revista e suas lições. Confirme apenas se deseja continuar.'
                : 'Esta acao remove o cadastro de segmento. A API valida vinculos antes da exclusao.'}
            </p>
          </div>
        </div>
      </FullScreenModal>
    </section>
  );
});

GeneralSettingsPanel.displayName = 'GeneralSettingsPanel';

export default GeneralSettingsPanel;
